/**
 * Tri-state approval. The portal owns the approval decision; GitHub owns the work
 * state (see `buckets.ts`). Nothing here reads the database — the caller supplies the
 * observed current status and gets a decision back.
 */
/** Ordered for display: the undecided state reads first. */
export const APPROVAL_STATUSES = ['pending', 'approved', 'not_approved'];
export const APPROVAL_STATUS_LABELS = {
    pending: 'Pending',
    approved: 'Approved',
    not_approved: 'Not approved',
};
/**
 * Written as an allow-list ("gate closes only if X"), never as a deny-conjunction: a
 * missing or unexpected field must leave the gate CLOSED, so the worst case is an
 * unnecessary approval prompt rather than unapproved billable work.
 *
 * Maintenance and bug work is never gated. `needs_triage` is not gated either: an unclassified
 * item must not open a billable approval flow off the back of a classifier failure (see
 * `llm/classify.ts`). It is still not cleared to start — `deriveBucket` holds it in Backlog
 * until a human classifies it.
 *
 * IMPORTED WORK IS GATED ONLY WHILE IT IS STILL OPEN, and the two halves of that have
 * different reasons.
 *
 * Open: a feature sitting unfinished in a client's repository is scope that has not been
 * delivered and has not been agreed. It is the same thing the portal exists to put a decision
 * in front of, and where it was raised does not change that.
 *
 * Closed: the work is done. A "Pending" marker there demands a decision on delivered history,
 * which is the failure the imported flag was introduced to prevent.
 *
 * The open-only test is deliberately confined to imported work. Applying it to everything
 * would un-gate a CLOSED declined feature, and `deriveBucket` leans on the gate to keep
 * `not_approved` terminal — a refused quote closed upstream as cleanup would start reading as
 * Delivered, which is the difference between a refusal and an invoice.
 */
/**
 * The actor on an audit row nobody clicked.
 *
 * Every other approval-audit row names a person, because every other one records a decision
 * somebody made. The gate-open row records that the gate OPENED — the classifier decided this
 * is a feature, so a decision is now owed — and there is no person to name. A literal typed at
 * the call site would drift the first time a second writer appears, and this string is read by
 * humans in the approval history.
 */
export const CLASSIFIER_ACTOR_LABEL = 'Portal classifier';
export function requiresApproval({ kind, imported, issueState }) {
    if (kind !== 'feature')
        return false;
    if (imported !== true)
        return true;
    return issueState === 'open';
}
/**
 * Approval is a one-way door: `pending` resolves to `approved` or `not_approved` and stops
 * there. Re-deciding is a new request, not a transition — the audit log is append-only, so
 * an "undo" would have to be recorded as history rather than erased.
 */
export function canTransition(from, to) {
    if (from !== 'pending')
        return false;
    return to === 'approved' || to === 'not_approved';
}
/**
 * Resolve an approval against the status observed at write time. Two people deciding the
 * same request concurrently is normal, so losing that race is a **no-op, not an error**:
 * the caller drops the write and shows the winning decision. Surfacing it as a failure
 * would invite a retry that overwrites a real decision.
 */
export function resolveApproval(current, next) {
    if (next !== 'approved' && next !== 'not_approved') {
        return { ok: false, reason: 'invalid_target', current };
    }
    if (current !== 'pending') {
        return { ok: false, reason: 'already_resolved', current };
    }
    return { ok: true, from: 'pending', to: next };
}
//# sourceMappingURL=approval.js.map